import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useApi } from '@/context/AuthContext';
import { formatIndianCurrency, INDIAN_STATES, validateGSTIN, getErrorMessage } from '@/utils/helpers';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Users } from 'lucide-react';

export default function CustomersPage() {
  const api = useApi();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gstin: '',
    pan: '',
    state_code: '',
    billing_address: { line1: '', city: '', state: '', pincode: '' },
    payment_terms: 30
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      gstin: '',
      pan: '',
      state_code: '',
      billing_address: { line1: '', city: '', state: '', pincode: '' },
      payment_terms: 30
    });
    setEditingCustomer(null);
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      email: customer.email || '',
      phone: customer.phone || '',
      gstin: customer.gstin || '',
      pan: customer.pan || '',
      state_code: customer.state_code || '',
      billing_address: customer.billing_address || { line1: '', city: '', state: '', pincode: '' },
      payment_terms: customer.payment_terms || 30
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    
    if (formData.gstin && !validateGSTIN(formData.gstin)) {
      toast.error('Invalid GSTIN format');
      return;
    }

    try {
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, formData);
        toast.success('Customer updated');
      } else {
        await api.post('/customers', formData);
        toast.success('Customer added');
      }
      fetchCustomers();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save customer'));
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await api.delete(`/customers/${customerId}`);
      toast.success('Customer deleted');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.gstin?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-stone-200 rounded w-1/3"></div>
          <div className="h-64 bg-stone-200 rounded"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="customers-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-stone-900 font-['Outfit']">Customers</h1>
            <p className="text-stone-600">Manage your customer directory</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1D9E75] hover:bg-[#178a65] text-white btn-press gap-2" data-testid="add-customer-btn">
                <Plus className="w-4 h-4" /> Add Customer
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-['Outfit']">
                  {editingCustomer ? 'Edit Customer' : 'Add Customer'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="customer-form">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Customer Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Acme Corp"
                      required
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="contact@acme.com"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <div>
                    <Label>GSTIN</Label>
                    <Input
                      value={formData.gstin}
                      onChange={(e) => setFormData(prev => ({ ...prev, gstin: e.target.value.toUpperCase() }))}
                      placeholder="27AABCU9603R1ZM"
                      className="font-mono"
                      data-testid="customer-gstin-input"
                    />
                  </div>
                  <div>
                    <Label>PAN</Label>
                    <Input
                      value={formData.pan}
                      onChange={(e) => setFormData(prev => ({ ...prev, pan: e.target.value.toUpperCase() }))}
                      placeholder="AABCU9603R"
                      className="font-mono"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>State</Label>
                    <Select 
                      value={formData.state_code} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, state_code: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDIAN_STATES.map(state => (
                          <SelectItem key={state.code} value={state.code}>
                            {state.code} - {state.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>Billing Address</Label>
                    <Input
                      value={formData.billing_address.line1}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        billing_address: { ...prev.billing_address, line1: e.target.value } 
                      }))}
                      placeholder="Street address"
                      className="mb-2"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={formData.billing_address.city}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          billing_address: { ...prev.billing_address, city: e.target.value } 
                        }))}
                        placeholder="City"
                      />
                      <Input
                        value={formData.billing_address.pincode}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          billing_address: { ...prev.billing_address, pincode: e.target.value } 
                        }))}
                        placeholder="Pincode"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Payment Terms (days)</Label>
                    <Input
                      type="number"
                      value={formData.payment_terms}
                      onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: parseInt(e.target.value) || 30 }))}
                      min="0"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#1D9E75] hover:bg-[#178a65] text-white" data-testid="save-customer-btn">
                    {editingCustomer ? 'Update' : 'Add'} Customer
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              placeholder="Search customers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Table */}
        {filteredCustomers.length > 0 ? (
          <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>GSTIN</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell className="font-medium">{customer.name}</TableCell>
                    <TableCell className="font-mono text-sm">{customer.gstin || '-'}</TableCell>
                    <TableCell>{customer.email || '-'}</TableCell>
                    <TableCell>{customer.phone || '-'}</TableCell>
                    <TableCell className="text-right font-mono">
                      {formatIndianCurrency(customer.outstanding_amount || 0)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(customer)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(customer.id)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-stone-200 p-12">
            <div className="text-center">
              <Users className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-stone-700 mb-2">No customers yet</h3>
              <p className="text-stone-500 mb-6">Add your first customer to start invoicing</p>
              <Button onClick={() => setDialogOpen(true)} className="bg-[#1D9E75] hover:bg-[#178a65] text-white gap-2">
                <Plus className="w-4 h-4" /> Add Customer
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
